<template>
  <div v-if="forceOrderItemsVisibility" class="space-y-2">
    <div>
      <div
        class="bg-white rounded text-sm"
        :class="[
          {
            'border-l-4': productType !== null,
            'border-gray-600': productType === 'package',
            'border-blue-600': productType === 'basic',
            'border-yellow-500': productType === 'service',
          },
        ]"
      >
        <div>
          <div class="p-4 space-y-4 hover:bg-gray-100">
            <div class="flex -mx-4 justify-between items-center">
              <div class="flex-1 px-4">
                <h5 class="text-sm font-semibold tracking-wide">
                  {{ orderItem.name }}
                </h5>
                <div class="flex space-x-2 mt-2">
                  <BaseButton
                    v-if="producable && hasProducable"
                    success
                    small
                    @click="editProducable"
                  >
                    Edit {{ producable }}
                  </BaseButton>
                  <OrderItemDetailsManager
                    v-if="
                      orderItem.requirements ||
                      orderItem.linksCount ||
                      orderItem.notesCount ||
                      orderItem.mediaCount
                    "
                    :order-item-id="orderItem.uuid"
                  />
                </div>
              </div>
              <div class="w-48 text-right px-4">
                <OrderItemStatusManager
                  :order-item-id="orderItem.uuid"
                  :status="orderItem.status"
                />
              </div>
              <div class="flex w-64 px-4">
                <ul class="ml-auto flex items-center space-x-2">
                  <li class="relative" v-if="orderItem.requirements" @click="toggleRequirements">
                    <span
                      class="inline-block p-2 rounded-full leading-none"
                      :class="[
                        {
                          'bg-gray-800': forceRequirementsVisibility || requirementsVisible,
                          'bg-gray-200': !forceRequirementsVisibility && !requirementsVisible,
                          'text-gray-200': forceRequirementsVisibility || requirementsVisible,
                          'text-gray-800': !forceRequirementsVisibility && !requirementsVisible,
                        },
                      ]"
                    >
                      <svg
                        fill="none"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        class="w-5 h-5 inline-flex focus:outline-none"
                        v-tip="{ content: 'Specs' }"
                      >
                        <path d="M4 6h16M4 12h16M4 18h7" />
                      </svg>
                    </span>
                  </li>
                  <li v-if="tasksCount" class="relative" @click="toggleTasks">
                    <span
                      class="inline-flex items-center space-x-1 p-2 rounded-full leading-none"
                      :class="[
                        {
                          'bg-gray-800': forceTasksVisibility || tasksVisible,
                          'bg-gray-200': !forceTasksVisibility && !tasksVisible,
                          'text-gray-200': forceTasksVisibility || tasksVisible,
                          'text-gray-800': !forceTasksVisibility && !tasksVisible,
                        },
                      ]"
                    >
                      <svg
                        fill="none"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        class="w-5 h-5 inline-flex focus:outline-none"
                        v-tip="{ content: 'Tasks' }"
                      >
                        <path
                          d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2
                        2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0
                        012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
                        />
                      </svg>
                      <div>
                        {{ tasksCount }}
                      </div>
                    </span>
                  </li>
                  <li v-if="isRootOrderItem">
                    <BaseButton primary outline small type="button" @click="addTask">
                      Add Task
                    </BaseButton>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div class="flex overflow-x-auto">
            <table class="table-fixed w-full min-w-screen-lg">
              <thead>
                <tr>
                  <th
                    v-if="showRequirements"
                    class="bg-gray-200 px-4 py-2 border border-l-0 text-xs font-normal"
                  >
                    Specs
                  </th>
                  <th
                    v-if="showTasks && isRootOrderItem"
                    class="bg-gray-200 px-4 py-2 border border-r-0 text-xs font-normal"
                    style="width: 32rem"
                  >
                    Tasks ({{ tasksCount }})
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td v-if="showRequirements" class="align-top p-4 border border-l-0 border-b-0">
                    <div class="prose prose-sm" v-html="requirements" />
                  </td>
                  <td
                    v-if="showTasks && isRootOrderItem"
                    class="align-top p-4 border border-r-0 border-b-0 bg-gray-100"
                  >
                    <table class="table-fixed w-full">
                      <tbody>
                        <tr>
                          <td>
                            <div class="space-y-2">
                              <TaskLine
                                v-for="task in tasks"
                                :key="task.uuid"
                                :task="task"
                                @updated="$emit('updated:task', task.uuid)"
                              />
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
    <div class="pl-8">
      <OrderItem
        v-for="childOrderItem in childOrderItems"
        :key="childOrderItem.uuid"
        :order-item="childOrderItem"
        :force-order-items-visibility="forceOrderItemsVisibility"
        :force-requirements-visibility="forceRequirementsVisibility"
        :force-tasks-visibility="forceTasksVisibility"
      />
    </div>
  </div>
  <div v-else>
    <div class="space-y-2">
      <TaskLine
        v-for="task in tasks"
        :key="task.uuid"
        :task="task"
        @updated="$emit('updated:task', task.uuid)"
      />

      <OrderItem
        v-for="childOrderItem in childOrderItems"
        :key="childOrderItem.uuid"
        :order-item="childOrderItem"
        :force-order-items-visibility="forceOrderItemsVisibility"
        :force-requirements-visibility="forceRequirementsVisibility"
        :force-tasks-visibility="forceTasksVisibility"
      />
    </div>
  </div>
</template>

<script>
import { marked } from 'marked';
import TaskLine from 'Components/ProjectTasks/TaskLine.vue';
import OrderItemStatusManager from './OrderItemStatusManager.vue';
import OrderItemDetailsManager from './order-item/order-item-details-manager.vue';

export default {
  name: 'OrderItem',
  components: {
    TaskLine,
    OrderItemStatusManager,
    OrderItemDetailsManager,
  },
  props: {
    orderItem: {
      type: Object,
      required: true,
    },
    forceOrderItemsVisibility: Boolean,
    forceRequirementsVisibility: Boolean,
    forceTasksVisibility: Boolean,
  },
  data() {
    return {
      orderItemsVisible: false,
      requirementsVisible: false,
      tasksVisible: false,
    };
  },
  computed: {
    isRootOrderItem() {
      return !this.orderItem.parentOrderItemUuid;
    },
    requirements() {
      return marked(this.orderItem.requirements || 'No specs');
    },
    showRequirements() {
      return this.forceRequirementsVisibility || this.requirementsVisible;
    },
    order() {
      return this.$store.state.entities.orders.byId[this.orderItem.orderUuid];
    },
    tasks() {
      return this.$store.getters['ui/projectTasks/tasksByProject'](this.orderItem.projectId).filter(
        (task) => task.orderItemId === this.orderItem.uuid
      );
    },
    showTasks() {
      return this.forceTasksVisibility || this.tasksVisible;
    },
    tasksCount() {
      return this.tasks.length;
    },
    product() {
      return this.$store.state.entities.products.byId[this.orderItem.product];
    },
    productType() {
      return this.product?.type || null;
    },
    childOrderItems() {
      return this.$store.getters['ui/orderOrderItems/orderItemsByOrder'](
        this.orderItem.orderUuid
      ).filter((child) => child.parentOrderItemUuid === this.orderItem.uuid);
    },
    producable() {
      if (!this.product || !this.product.producesModelType) {
        return null;
      }

      const producables = {
        campaigns: 'Ad plan',
        pages: 'Page',
        media: 'Creative',
      };

      return producables[this.product.producesModelType] || null;
    },
    hasProducable() {
      return (
        typeof this.orderItem.producableType === 'string' &&
        typeof this.orderItem.producableId === 'string'
      );
    },
  },
  methods: {
    toggleRequirements() {
      this.requirementsVisible = !this.requirementsVisible;
    },
    toggleTasks() {
      this.tasksVisible = !this.tasksVisible;
    },
    addTask() {
      this.$modal.show({
        component: () => import('@/components/ProjectTasks/CreateTaskModal.vue'),
        props: {
          projectId: this.orderItem.projectId,
          defaultData: { orderItemId: this.orderItem.uuid, title: this.orderItem.name },
        },
        on: {
          created: this.editTask,
        },
        hideEvent: 'close',
      });
    },
    editTask(taskId) {
      this.$modal.show({
        component: () => import('@/components/Tasks/TaskModal.vue'),
        props: {
          taskId,
        },
        on: {
          'status-updated': () => {
            this.$emit('updated:task', taskId);
          },
        },
        hideEvent: 'task-modal:closed',
      });
    },
    editProducable() {
      if (this.orderItem.producableType !== 'campaigns') {
        return;
      }

      this.$modal.show({
        component: 'CampaignModal',
        props: {
          campaignId: this.orderItem.producableId,
        },
        on: {},
        hideEvent: 'campaign-modal-close',
      });
    },
  },
};
</script>
